import { Offcanvas, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { EditEntityDrawerProps } from './EntityListProps';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormTextAreaField from './formFields/FormTextAreaField';
import { Modal } from 'react-bootstrap';
import { validateDateOrder } from '../../utils/validateDateOrder';
import { BsMicrosoftTeams } from 'react-icons/bs';
import { useIsEntraCapable } from 'hooks/useIsEntraCapable';

import {
  eventTypes,
  IncidentCalendarDisplayEvent,
  useEditIncidentCalendarEvent,
  useDeleteIncidentCalendarEvent,
} from './hooks/incidentCalendarHooks';

import FormDateTimeField from './formFields/FormDateTimeField';
import FormSelectField from './formFields/FormSelectField';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { StylishNewChip } from 'components/DesignSystems/New/StylishNewChip';
import CalendarEventAddAttendeeDialog from './CalendarEventAddAttendeeDialog';

const CalendarEventEditDrawer: React.FC<
  EditEntityDrawerProps<IncidentCalendarDisplayEvent>
> = ({ toggle, selectedEntity }) => {
  const editedEntity = {
    start: selectedEntity.start,
    end: selectedEntity.end,
    title: selectedEntity.title,
    externalAttendees: selectedEntity.externalAttendees,
    location: selectedEntity.location,
    id: selectedEntity.id,
    purpose: selectedEntity.purpose,
    color: selectedEntity.color,
    internalAttendees: selectedEntity.internalAttendees || [],
    isOnlineMeeting: selectedEntity.isOnlineMeeting ?? false,
    teamsJoinUrl: selectedEntity.teamsJoinUrl ?? null,
  };

  const form = useForm<IncidentCalendarDisplayEvent>({
    mode: 'onChange',
    defaultValues: {
      ...editedEntity,
    },
  });
  const { handleSubmit, watch, setValue, getValues, formState } = form;

  const isEntraCapable = useIsEntraCapable();
  const color = watch('color');
  const startValue = watch('start');
  const endValue = watch('end');
  const isOnlineMeeting = watch('isOnlineMeeting');
  const teamsJoinUrl = watch('teamsJoinUrl');

  useEffect(() => {
    if (color && formState.dirtyFields.color) {
      const eventType = eventTypes?.find((type) => type.code === color);
      if (eventType) {
        setValue('purpose', eventType.agenda);
        setValue('title', eventType.title);
      }
    }
  }, [color, setValue, formState.dirtyFields.color]);

  useEffect(() => {
    if (startValue && endValue) {
      form.trigger(['start', 'end']);
    }
  }, [startValue, endValue, form]);

  const mutation = useEditIncidentCalendarEvent();
  const deleteCalendarEvent = useDeleteIncidentCalendarEvent();

  const { data: incidentRoster } = useGetRosterForIncident();

  const [incidentMembersToAttend, setIncidentMembersToAttend] = useState(
    editedEntity.internalAttendees || []
  );

  const [showAddAttendeeDialog, setShowAddAttendeeDialog] = useState(false);

  const handleRecipientDeleteClick = (memberGuid: string) => {
    setIncidentMembersToAttend((prev) =>
      prev.filter((guid: string) => guid !== memberGuid)
    );
  };

  const submitEventEdit = async (formData: IncidentCalendarDisplayEvent) => {
    const payload = {
      ...formData,
      internalAttendees: [...incidentMembersToAttend],
      id: editedEntity.id,
    };

    try {
      await mutation.mutateAsync(payload);
    } catch {
      // Error toast handled by onError callback; drawer stays open for retry
      return;
    }
    toggle();
  };

  const handleConfirmDelete = async () => {
    if (editedEntity.id) {
      try {
        await deleteCalendarEvent.mutateAsync(editedEntity.id);
      } catch {
        // Error toast handled by onError callback; drawer stays open for retry
        return;
      }
      toggle();
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  return (
    <>
      <DrawerWrapper toggle={toggle} title="Edit Event">
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(submitEventEdit)}
            className="d-flex flex-column justify-content-between flex-grow-1"
          >
            <Offcanvas.Body>
              <Row>
                <FormSelectField
                  title="Event Type"
                  fieldName="color"
                  classes="border-color-change"
                  style={{
                    borderColor: color,
                  }}
                  options={eventTypes?.map((type) => {
                    return {
                      label: type.title,
                      value: type.code,
                    };
                  })}
                  variant="half"
                  rules={{
                    required: {
                      value: true,
                      message: 'Event type is required',
                    },
                  }}
                />
                <FormTextField
                  title="Title"
                  fieldName="title"
                  rules={{
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    maxLength: {
                      value: 255,
                      message: 'This maximum length is 255 characters',
                    },
                  }}
                  variant="half"
                />
              </Row>
              <Row>
                <FormDateTimeField
                  title="Start Date & Time"
                  fieldName="start"
                  variant="full"
                  rules={{
                    required: {
                      value: true,
                      message: 'Start date and time is required',
                    },
                    validate: (value: string) => {
                      return validateDateOrder(value, endValue);
                    },
                  }}
                />
              </Row>
              <Row>
                <FormDateTimeField
                  title="End Date & Time"
                  fieldName="end"
                  variant="full"
                  rules={{
                    required: {
                      value: true,
                      message: 'End date and time is required',
                    },
                    validate: (value: string) => {
                      return validateDateOrder(startValue, value);
                    },
                  }}
                />
              </Row>
              <Row>
                <p className="mb-2">Incident Roster Attendees</p>
                {(!!incidentMembersToAttend &&
                  !!incidentMembersToAttend.length && (
                    <>
                      <div className="form-chip-group mb-4">
                        {incidentMembersToAttend
                          .filter((memberGuid: string) =>
                            incidentRoster?.find(
                              (person) => person.userId === memberGuid
                            )
                          )
                          .map((memberGuid: string) => {
                            const user = incidentRoster?.find(
                              (person) => person.userId === memberGuid
                            );
                            const name =
                              user?.userName || (user?.emailAddress as string);
                            return (
                              <>
                                <StylishNewChip
                                  key={`key-${memberGuid}`}
                                  label={name || ''}
                                  handleDelete={() => {
                                    handleRecipientDeleteClick(memberGuid);
                                  }}
                                />
                              </>
                            );
                          })}
                      </div>
                    </>
                  )) || (
                  <p className="text-muted">
                    No incident roster attendees selected
                  </p>
                )}
                <StylishNewButton
                  className="button--secondary"
                  onClick={() => {
                    // Logic to open a modal or another drawer to select incident members
                    setShowAddAttendeeDialog(true);
                  }}
                >
                  Add Incident Members
                </StylishNewButton>
              </Row>
              <Row>
                <FormTextAreaField
                  title="External Attendees"
                  fieldName="externalAttendees"
                  rules={{
                    maxLength: {
                      value: 255,
                      message: 'This maximum length is 255 characters',
                    },
                  }}
                  variant="full"
                />
              </Row>
              <Row>
                <FormTextAreaField
                  title="Agenda"
                  fieldName="purpose"
                  rules={{
                    required: {
                      value: true,
                      message: 'This field is required',
                    },
                    maxLength: {
                      value: 5000,
                      message: 'This maximum length is 5000 characters',
                    },
                  }}
                  variant="full"
                />
              </Row>
              <Row className="align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <BsMicrosoftTeams size={20} color="#6264A7" />
                  <span>Teams meeting</span>
                  {isEntraCapable ? (
                    <div className="form-switcher ms-2">
                      <input
                        type="checkbox"
                        id="teams-meeting-toggle-edit"
                        aria-label="Teams meeting"
                        checked={!!isOnlineMeeting}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setValue('isOnlineMeeting', checked, {
                            shouldDirty: true,
                          });
                          if (checked) {
                            setValue('location', 'Teams Meeting', {
                              shouldDirty: true,
                            });
                          } else if (
                            getValues('location') === 'Teams Meeting'
                          ) {
                            setValue('location', '', {
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                      <label htmlFor="teams-meeting-toggle-edit"></label>
                    </div>
                  ) : (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="teams-meeting-toggle-edit-tooltip">
                          Sign in with Microsoft to auto-create a Teams meeting.
                          You can still paste an existing Teams meeting URL
                          below.
                        </Tooltip>
                      }
                    >
                      <span
                        className="form-switcher d-inline-block ms-2"
                        tabIndex={0}
                        aria-describedby="teams-meeting-toggle-edit-tooltip"
                      >
                        <input
                          type="checkbox"
                          id="teams-meeting-toggle-edit"
                          aria-label="Teams meeting"
                          checked={!!isOnlineMeeting}
                          disabled
                          readOnly
                        />
                        <label htmlFor="teams-meeting-toggle-edit"></label>
                      </span>
                    </OverlayTrigger>
                  )}
                </div>
              </Row>
              {(isOnlineMeeting || !!teamsJoinUrl || !isEntraCapable) && (
                <>
                  <FormTextField
                    title="Teams Join URL"
                    fieldName="teamsJoinUrl"
                    rules={{
                      maxLength: {
                        value: 2048,
                        message: 'This maximum length is 2048 characters',
                      },
                      validate: (value: string) => {
                        const trimmed = value?.trim();
                        if (!trimmed) {
                          return true;
                        }
                        try {
                          const url = new URL(trimmed);
                          if (
                            url.protocol !== 'http:' &&
                            url.protocol !== 'https:'
                          ) {
                            return 'URL must start with http:// or https://';
                          }
                          return true;
                        } catch {
                          return 'Please enter a valid URL';
                        }
                      },
                    }}
                    variant="full"
                  />
                  <small className="text-muted d-block mb-3">
                    {isEntraCapable
                      ? 'Leave blank to have a Teams meeting created for you on save, or paste a custom join URL.'
                      : 'Paste an existing Teams meeting URL to attach it to this event.'}
                  </small>
                </>
              )}
              <FormTextField
                title="Location"
                fieldName="location"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 255,
                    message: 'This maximum length is 255 characters',
                  },
                }}
                variant="half"
              />
            </Offcanvas.Body>
            <DrawerFooter>
              <div className="button-group">
                <StylishNewButton
                  className="button--tertiary button--reverse"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary button--reverse"
                  type="button"
                  onClick={toggle}
                >
                  Close
                </StylishNewButton>
                <StylishNewButton
                  className="button--primary"
                  type="submit"
                  disabled={mutation.isPending}
                >
                  Save
                </StylishNewButton>
              </div>
            </DrawerFooter>
          </form>
        </FormProvider>
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
          backdrop={true}
          keyboard={true}
        >
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>Delete Event</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete this event?</p>
            <p>Title: {selectedEntity.title}</p>
            <p>Purpose: {selectedEntity.purpose}</p>
          </Modal.Body>
          <Modal.Footer>
            <StylishNewButton
              className="button--secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton
              className="button--tertiary"
              onClick={handleConfirmDelete}
            >
              Delete
            </StylishNewButton>
          </Modal.Footer>
        </Modal>
      </DrawerWrapper>
      {showAddAttendeeDialog && (
        <CalendarEventAddAttendeeDialog
          onClose={() => setShowAddAttendeeDialog(false)}
          incidentRoster={incidentRoster || []}
          incidentMembersToAttend={incidentMembersToAttend}
          setIncidentMembersToAttend={setIncidentMembersToAttend}
        />
      )}
    </>
  );
};

export default CalendarEventEditDrawer;
